/* Boonsoir 2.0 — hero: Boonsoir-tijd (real Amsterdam time on the engraved clock + adaptive line),
   idle loops (steam, lamp, pouches) that pause off-screen, and pointer depth on fine pointers.
   One curve everywhere (var(--ease) in CSS). Re-inits on shopify:section:load. */
(() => {
  'use strict';
  if (window.BoonsoirHero) return;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const TICK = 20000;

  const fmt = new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });
  function amsterdamNow() {
    const parts = {};
    fmt.formatToParts(new Date()).forEach((p) => { parts[p.type] = p.value; });
    return { h: +parts.hour % 24, m: +parts.minute, s: +parts.second };
  }
  const pad = (n) => String(n).padStart(2, '0');

  function lineFor(t, tpl) {
    const time = `${pad(t.h)}:${pad(t.m)}`;
    if (t.h >= 15) return tpl.during.replace(/\{time\}/g, time);
    if (t.h < 6) return tpl.night.replace(/\{time\}/g, time);
    const left = 15 * 60 - (t.h * 60 + t.m);
    const hrs = Math.floor(left / 60);
    const min = left % 60;
    const minTxt = `${min} ${min === 1 ? 'minuut' : 'minuten'}`;
    const leftTxt = hrs && min ? `${hrs} uur en ${minTxt}` : hrs ? `${hrs} uur` : minTxt;
    return tpl.before.replace(/\{left\}/g, leftTxt).replace(/\{time\}/g, time);
  }

  // Accumulating angles: a hand only ever turns clockwise, so 59 → 00 never spins backwards.
  function forward(prev, target) {
    if (prev == null) return target;
    const d = ((target - prev) % 360 + 360) % 360;
    return prev + d;
  }

  class Hero {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-hero-stage]');
      this.clock = root.querySelector('[data-hero-clock]');
      this.line = root.querySelector('[data-hero-line]');
      this.hourHand = this.clock && this.clock.querySelector('.hero__hand--h');
      this.minHand = this.clock && this.clock.querySelector('.hero__hand--m');
      this.tpl = { before: root.dataset.tplBefore || '', during: root.dataset.tplDuring || '', night: root.dataset.tplNight || '' };
      this.angles = { h: null, m: null };
      this.visible = true;
      this.timers = [];
      this.observers = [];
      this.still = reduced.matches || Boolean(window.Shopify && window.Shopify.designMode);
      // Daypart (set before first paint in theme.liquid): after 15:00 the lamp goes on as the first act of the intro.
      this.part = document.documentElement.getAttribute('data-daypart') || 'day';
      if (this.part !== 'day') root.classList.add('is-lit');
      this.onVis = () => this.syncIdle(true);
      document.addEventListener('visibilitychange', this.onVis);
      // idle loops also rest under the open menu sheet / cart drawer
      this.overlays = [...document.querySelectorAll('[data-menu-sheet], [data-cart-drawer]')];
      if (this.overlays.length && 'MutationObserver' in window) {
        this.overlayMo = new MutationObserver(() => this.syncIdle());
        this.overlays.forEach((el) => this.overlayMo.observe(el, { attributes: true, attributeFilter: ['class'] }));
      }

      if (root.hasAttribute('data-clock')) {
        this.writeLine(amsterdamNow(), true);
        this.interval = setInterval(() => this.tick(), TICK);
      }
      if (this.still) {
        root.classList.add('is-go', 'is-printed');
        if (this.clock) { this.clock.classList.add('is-live'); this.setHands(amsterdamNow(), true); }
      } else if (document.documentElement.classList.contains('vt-arrival')) {
        // arriving by page transition: the scene is already "on", no second intro
        root.classList.add('is-go', 'is-printed', 'is-idle');
        if (this.clock) { this.clock.classList.add('is-live'); this.setHands(amsterdamNow(), true); }
      } else {
        this.choreograph();
      }
      this.startIdle();
      this.startPointer();
    }

    later(fn, ms) { this.timers.push(setTimeout(fn, ms)); }

    // Watch an attribute until test() passes (or the fallback time runs out), then run fn once.
    when(el, attr, test, fn, fallback) {
      let done = false;
      let mo = null;
      const go = () => { if (done) return; done = true; if (mo) mo.disconnect(); fn(); };
      if (!el || test()) { go(); return; }
      mo = new MutationObserver(() => { if (test()) go(); });
      mo.observe(el, { attributes: true, attributeFilter: [attr] });
      this.observers.push(mo);
      this.later(go, fallback);
    }

    /* ---------- Intro: headline lines → pouches one by one; ink print → hands sweep to now ---------- */
    choreograph() {
      const root = this.root;
      const title = root.querySelector('.hero__title');
      this.when(title, 'class', () => title.classList.contains('is-in'), () => root.classList.add('is-go'), 1600);

      const ill = root.querySelector('.hero__ill');
      const printed = () => {
        root.classList.add('is-printed');
        this.later(() => this.sweep(), 200);
        this.later(() => { root.classList.add('is-idle'); this.syncIdle(); }, 1200);
      };
      // motion.js prints img.ill (data-ink: wait → run → removed). The wipe passes the clock ~420 ms into the print.
      const onRun = () => {
        const state = ill && ill.getAttribute('data-ink');
        if (state === 'run') { this.later(printed, (parseFloat(ill.style.getPropertyValue('--ink-d')) || 0) + 420); return; }
        printed();
      };
      const settled = () => !ill || ill.getAttribute('data-ink') !== 'wait';
      if (ill && ill.hasAttribute('data-ink')) this.when(ill, 'data-ink', settled, onRun, 3200);
      else this.later(() => this.when(ill, 'data-ink', settled, onRun, 3200), 120);
    }

    sweep() {
      if (!this.clock) return;
      this.clock.classList.add('is-sweep');
      this.setHands(amsterdamNow());
      this.later(() => { this.clock.classList.remove('is-sweep'); this.clock.classList.add('is-live'); }, 950);
    }

    tick() {
      const t = amsterdamNow();
      this.setHands(t);
      this.writeLine(t, false);
    }

    setHands(t, instant) {
      if (!this.hourHand) return;
      const m = t.m * 6 + t.s * 0.1;
      const h = (t.h % 12) * 30 + t.m * 0.5 + t.s / 120;
      this.angles.m = forward(this.angles.m, m);
      this.angles.h = forward(this.angles.h, h);
      if (instant) { this.angles.m = m; this.angles.h = h; }
      this.minHand.style.setProperty('--deg', `${this.angles.m.toFixed(2)}deg`);
      this.hourHand.style.setProperty('--deg', `${this.angles.h.toFixed(2)}deg`);
    }

    writeLine(t, first) {
      if (!this.line || !this.tpl.during) return;
      const text = lineFor(t, this.tpl);
      if (this.line.textContent !== text) this.line.textContent = text;
      // Not a live region: screen readers read the line in place, and minute updates stay silent.
    }

    /* ---------- Idle loops ---------- */
    startIdle() {
      if (reduced.matches) return;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([e]) => { this.visible = e.isIntersecting; this.syncIdle(true); });
        this.io.observe(this.root);
      }
    }

    syncIdle(fromEvent) {
      const covered = this.overlays && this.overlays.some((el) => el.classList.contains('is-open'));
      const run = this.visible && !document.hidden && !covered;
      this.root.classList.toggle('is-paused', !run);
      if (fromEvent && run && this.root.hasAttribute('data-clock')) this.tick();
    }

    /* ---------- Pointer depth (illustration ±6px, glow ±3px, pouches ±14px; opposite to the cursor) ---------- */
    startPointer() {
      if (!this.stage || reduced.matches || !finePointer.matches) return;
      this.target = { x: 0, y: 0 };
      this.cur = { x: 0, y: 0 };
      this.raf = 0;
      this.onMove = (e) => {
        const r = this.root.getBoundingClientRect();
        this.target.x = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1));
        this.target.y = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1));
        this.kick();
      };
      this.onLeave = () => { this.target.x = 0; this.target.y = 0; this.kick(); };
      this.root.addEventListener('pointermove', this.onMove, { passive: true });
      this.root.addEventListener('pointerleave', this.onLeave);
    }

    kick() { if (!this.raf) this.raf = requestAnimationFrame(() => this.frame()); }

    frame() {
      this.raf = 0;
      const k = 0.09;
      this.cur.x += (this.target.x - this.cur.x) * k;
      this.cur.y += (this.target.y - this.cur.y) * k;
      const settled = Math.abs(this.target.x - this.cur.x) < 0.001 && Math.abs(this.target.y - this.cur.y) < 0.001;
      if (settled) { this.cur.x = this.target.x; this.cur.y = this.target.y; }
      this.stage.style.setProperty('--px', this.cur.x.toFixed(4));
      this.stage.style.setProperty('--py', this.cur.y.toFixed(4));
      if (!settled) this.kick();
    }

    destroy() {
      clearInterval(this.interval);
      this.timers.forEach(clearTimeout);
      if (this.io) this.io.disconnect();
      if (this.overlayMo) this.overlayMo.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      this.observers.forEach((o) => o.disconnect());
      document.removeEventListener('visibilitychange', this.onVis);
      if (this.onMove) { this.root.removeEventListener('pointermove', this.onMove); this.root.removeEventListener('pointerleave', this.onLeave); }
    }
  }

  const instances = new Map();
  function init(scope) {
    scope.querySelectorAll('[data-hero]').forEach((el) => { if (!instances.has(el)) instances.set(el, new Hero(el)); });
  }
  function teardown(scope) {
    instances.forEach((inst, el) => { if (scope.contains(el)) { inst.destroy(); instances.delete(el); } });
  }

  init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => teardown(e.target));
  window.BoonsoirHero = { amsterdamNow, lineFor };
})();
